import { z } from "zod";
import { Id, IsoDate, IsoDateTime, Minor } from "./common";
import { defineRoutes } from "./define";
import { PatientSnapshot } from "./patients";
import { PROBLEMS } from "./problem";

/**
 * Screen read models (PLAN §3 #5): one DTO per screen, mirrored 1:1 from
 * core/modules/{dashboard,shell,analytics,patients}/application. Names match the core
 * DTOs; `Date` fields are ISO strings here. Leaf enums/sub-schemas stay module-private so
 * the barrel never collides with the context contracts (appointments, invoices, …).
 */

// ---- leaf enums (private: the owning context contract exports the canonical ones) ------
const AppointmentStatus = z.enum(["booked", "done", "cancelled", "no_show"]);
const VisitPaymentStatus = z.enum(["unbilled", "invoiced", "paid", "package"]);
const ClinicalStatus = z.enum([
  "acute",
  "irritable",
  "controlled",
  "return_to_load",
  "maintenance",
]);
const TaskType = z.enum([
  "message_post_visit",
  "check_24h",
  "event_reminder",
  "offer_service",
  "update_plan",
  "request_media",
  "other",
]);
const TaskPriority = z.enum(["low", "medium", "high"]);
const TaskStatus = z.enum(["new", "in_progress", "done", "cancelled"]);
const VisitResult = z.enum(["better", "partial", "no_change", "worse", "await_24h"]);
const FileType = z.enum([
  "photo",
  "video",
  "mri",
  "ultrasound",
  "xray",
  "pdf",
  "training_screenshot",
  "movement_analysis",
  "other",
]);
const GiftCardStatus = z.enum(["active", "redeemed", "expired", "void"]);
const SaleInvoiceStatus = z.enum(["issued", "paid", "void"]);
const InvoiceStatus = z.enum(["issued", "paid", "void"]);
const PatientPackageStatus = z.enum(["active", "exhausted", "cancelled"]);
const Person = z.object({ id: Id, name: z.string() });

// ---- dashboard ----------------------------------------------------------------------------
export const DashboardView = z.enum(["specialist", "admin", "manager"]);
export type DashboardView = z.infer<typeof DashboardView>;

const AgendaItem = z.object({
  id: Id,
  patientId: Id,
  patientName: z.string(),
  episodeId: Id.nullable(),
  startsAt: IsoDateTime,
  durationMin: z.number().int(),
  status: AppointmentStatus,
  comment: z.string().nullable(),
  visitId: Id.nullable(),
  specialistId: Id.nullable(),
  specialistName: z.string().nullable(),
});

const FinanceSummary = z.object({
  unpaidCount: z.number().int(),
  unpaidMinor: Minor,
  activeGiftCount: z.number().int(),
  giftBalanceMinor: Minor,
  monthPaidMinor: Minor,
  unbilledCount: z.number().int(),
  unbilledBaseSumMinor: Minor,
});

const DashboardCounts = z.object({
  activePatients: z.number().int(),
  openTasks: z.number().int(),
  highPriorityTasks: z.number().int(),
  upcomingStarts: z.number().int(),
  visitsThisWeek: z.number().int(),
  painProgressPct: z.number(),
});

const NeedsActionItem = z.object({
  patientId: Id,
  patientName: z.string(),
  episodeTitle: z.string().nullable(),
  taskTitle: z.string(),
  priority: z.string(),
  dueDate: z.string().nullable(),
});

const UpcomingStart = z.object({
  patientId: Id,
  patientName: z.string(),
  eventName: z.string(),
  eventDate: z.string(),
  daysLeft: z.number().int(),
});

const ActivityItem = z.object({
  id: Id,
  action: z.string(),
  entity: z.string(),
  subject: z.string().nullable(),
  at: IsoDateTime,
});

const DashboardBase = z.object({
  hour: z.number().int().min(0).max(23),
  agenda: z.array(AgendaItem),
  payments: z.record(z.string(), VisitPaymentStatus),
  activity: z.array(ActivityItem),
});

export const ClinicalDashboardData = DashboardBase.extend({
  view: z.enum(["specialist", "admin"]),
  counts: DashboardCounts,
  needsAction: z.array(NeedsActionItem),
  upcomingStarts: z.array(UpcomingStart),
  finance: FinanceSummary.nullable(),
});

export const ManagerDashboardData = DashboardBase.extend({
  view: z.literal("manager"),
  finance: FinanceSummary,
});

export const DashboardData = z.discriminatedUnion("view", [
  ClinicalDashboardData,
  ManagerDashboardData,
]);
export type DashboardData = z.infer<typeof DashboardData>;

// ---- rail ---------------------------------------------------------------------------------
const RailPatient = z.object({
  id: Id,
  name: z.string(),
  sport: z.string().nullable(),
  status: z.string(),
  nextStart: z.string().nullable(),
  episodeTitle: z.string().nullable(),
  clinicalStatus: z.string().nullable(),
  painNow: z.number().nullable(),
  hasOpenTask: z.boolean(),
});

export const RailData = z.object({
  patients: z.array(RailPatient),
  patientCount: z.number().int(),
  openTaskCount: z.number().int(),
});
export type RailData = z.infer<typeof RailData>;

// ---- analytics ----------------------------------------------------------------------------
const AnalyticsSummary = z.object({
  totalPatients: z.number().int(),
  activePatients: z.number().int(),
  totalEpisodes: z.number().int(),
  totalVisits: z.number().int(),
  visitsThisWeek: z.number().int(),
  openTasks: z.number().int(),
  doneTasks: z.number().int(),
  taskCompletionPct: z.number(),
  painImprovementPct: z.number(),
});
const TrendPoint = z.object({
  weeksAgo: z.number().int(),
  label: z.string(),
  value: z.number().nullable(),
});
const Distribution = z.object({ key: z.string(), count: z.number().int() });

export const AnalyticsData = z.object({
  summary: AnalyticsSummary,
  visitsTrend: z.array(TrendPoint),
  painTrend: z.array(TrendPoint),
  patientStatusMix: z.array(Distribution),
  visitResultMix: z.array(Distribution),
  clinicalStatusMix: z.array(Distribution),
});
export type AnalyticsData = z.infer<typeof AnalyticsData>;

// ---- patient workspace --------------------------------------------------------------------
const PatientAppointmentItem = z.object({
  id: Id,
  startsAt: IsoDateTime,
  durationMin: z.number().int(),
  status: AppointmentStatus,
  comment: z.string().nullable(),
  visitId: Id.nullable(),
  specialistId: Id.nullable(),
  specialistName: z.string().nullable(),
});
const PatientInvoiceItem = z.object({
  id: Id,
  number: z.string(),
  status: InvoiceStatus,
  totalMinor: Minor,
  issuedAt: IsoDateTime,
});
const PatientPackageItem = z.object({
  id: Id,
  title: z.string(),
  visitsTotal: z.number().int(),
  visitsUsed: z.number().int(),
  status: PatientPackageStatus,
  priceMinor: Minor,
  purchasedAt: IsoDateTime,
  invoiceId: Id.nullable(),
});
const PackageItem = z.object({
  id: Id,
  specialistId: Id,
  title: z.string(),
  visitCount: z.number().int(),
  discountPct: z.number(),
  priceMinor: Minor.nullable(),
});
const PatientGiftItem = z.object({
  id: Id,
  code: z.string(),
  initialMinor: Minor,
  remainingMinor: Minor,
  expiresOn: IsoDate.nullable(),
  status: GiftCardStatus,
  invoiceId: Id.nullable(),
  saleInvoiceStatus: SaleInvoiceStatus.nullable(),
});

export const OperationalWorkspace = z.object({
  appointments: z.array(PatientAppointmentItem),
  invoices: z.array(PatientInvoiceItem),
  packages: z.array(PatientPackageItem),
  packageDefs: z.array(PackageItem),
  specialists: z.array(Person),
  gifts: z.array(PatientGiftItem),
  nextBookedAt: IsoDateTime.nullable(),
  owedMinor: Minor,
  giftBalanceMinor: Minor,
});

const ActiveEpisodeView = z.object({
  id: Id,
  title: z.string(),
  clinicalStatus: ClinicalStatus,
  stage: z.number().int(),
  hypothesis: z.string().nullable(),
  goal: z.string().nullable(),
  painStart: z.number().nullable(),
  painNow: z.number().nullable(),
  functionStart: z.string().nullable(),
  functionNow: z.string().nullable(),
  redFlags: z.string().nullable(),
  constraints: z.string().nullable(),
});
const LastVisitView = z.object({
  occurredAt: IsoDateTime,
  painBefore: z.number().nullable(),
  painAfter: z.number().nullable(),
  done: z.string().nullable(),
  worked: z.string().nullable(),
  notWorked: z.string().nullable(),
  checkNext: z.string().nullable(),
  nextPlan: z.string().nullable(),
});
const OpenTaskView = z.object({
  id: Id,
  title: z.string(),
  dueDate: z.string().nullable(),
  priority: TaskPriority,
});
const TodayBrief = z.object({
  patientName: z.string(),
  episode: ActiveEpisodeView.nullable(),
  lastVisit: LastVisitView.nullable(),
  openTasks: z.array(OpenTaskView),
});
const VisitHistoryItem = LastVisitView.extend({
  id: Id,
  result: VisitResult.nullable(),
  reaction: z.string().nullable(),
  homework: z.string().nullable(),
  write24: z.boolean(),
});
const TaskListItem = z.object({
  id: Id,
  title: z.string(),
  dueDate: z.string().nullable(),
  priority: TaskPriority,
  type: TaskType,
  status: TaskStatus,
  episodeId: Id.nullable(),
  assigneeId: Id.nullable(),
  comment: z.string().nullable(),
});
const FileListItem = z.object({
  id: Id,
  title: z.string(),
  type: FileType,
  href: z.string().nullable(),
  comment: z.string().nullable(),
  addedAt: IsoDateTime,
});
const EpisodeListItem = z.object({
  id: Id,
  title: z.string(),
  clinicalStatus: ClinicalStatus,
  stage: z.number().int(),
  painNow: z.number().nullable(),
  closedAt: IsoDateTime.nullable(),
});
const CompetitionListItem = z.object({
  id: Id,
  title: z.string(),
  date: IsoDate,
  comment: z.string().nullable(),
});
const ActivePackageView = z.object({
  id: Id,
  title: z.string(),
  visitsTotal: z.number().int(),
  visitsUsed: z.number().int(),
});
const RenderedTemplate = z.object({ key: z.string(), title: z.string(), text: z.string() });

export const ClinicalWorkspace = z.object({
  brief: TodayBrief,
  briefText: z.string(),
  history: z.array(VisitHistoryItem),
  paymentStatuses: z.record(z.string(), VisitPaymentStatus),
  openTasks: z.array(TaskListItem),
  files: z.array(FileListItem),
  episodes: z.array(EpisodeListItem),
  assignees: z.array(Person),
  competitions: z.array(CompetitionListItem),
  nextStart: CompetitionListItem.nullable(),
  activePackage: ActivePackageView.nullable(),
  templates: z.array(RenderedTemplate),
});

export const PatientWorkspace = z.object({
  patient: PatientSnapshot,
  todayISO: IsoDate,
  todayKyiv: IsoDate,
  operational: OperationalWorkspace,
  /** `null` for roles without `clinical:read` — decided by the API, never by the client. */
  clinical: ClinicalWorkspace.nullable(),
});
export type PatientWorkspace = z.infer<typeof PatientWorkspace>;

// ---- routes -------------------------------------------------------------------------------
export const readModels = defineRoutes({
  dashboard: {
    method: "GET",
    path: "/dashboard",
    summary:
      "The landing screen for the caller's role: managers always get the operational view, specialists the clinical one, admins may ask for ?view=admin (adds receivables)",
    query: z.object({ view: z.enum(["specialist", "admin"]).optional() }),
    responses: { 200: DashboardData, 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  rail: {
    method: "GET",
    path: "/rail",
    summary: "The shell's left rail: working patient list + counts",
    responses: { 200: RailData, 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  analytics: {
    method: "GET",
    path: "/analytics",
    summary: "Clinical KPIs and 8-week trends (clinical:read)",
    responses: { 200: AnalyticsData, 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  patientWorkspace: {
    method: "GET",
    path: "/patients/:patientId/workspace",
    summary: "Everything the patient screen renders; the clinical half only with clinical:read",
    params: z.object({ patientId: Id }),
    responses: {
      200: PatientWorkspace,
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
    },
  },
});
